import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { filter } from "~/tools/filter";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { pipe } from "~/tools/pipe";
import type { Template } from "~/tools/template";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import type { DashboardVariable, DashboardVariableValue } from "../../../../common/dashboard-spec";
import { useDashboardContext } from "../_dashboard-context";
import { generateVariableNames } from "../generate-variable-names";

import type { VariableState } from "./_common";
import { Header } from "./_header";
import { ListMultiChoiceVariable } from "./_list-multi-choice";
import { ListSingleChoiceVariable } from "./_list-single-choice";
import { MetricLabelNames } from "./_metric-label-names";
import { MetricLabelValues } from "./_metric-label-values";
import { MetricsPromQLVariable } from "./_metrics-promql";
import { TextVariable } from "./_text";

interface Props extends VariableState {
	onChange: (variable: DashboardVariable) => void;
}

export const Variable = ({ initialVariable, variable, onChange }: Props): React.JSX.Element => {
	const { state: dashboardState } = useDashboardContext();
	const templateVariables = useTemplateVariables();

	const label = variable.label ?? variable.name;
	const isDirty = !variable.isValueEqual(initialVariable);
	const variablesSavedInSpec = useComputed(
		(monitor) => dashboardState.form.fields.settings.getInput(monitor).saveVariables,
	);

	const header = (
		<Header
			canReset={variablesSavedInSpec && isDirty}
			label={label}
			onReset={() => {
				onChange(initialVariable);
			}}
		/>
	);

	if (variable.spec.kind === "TextVariable") {
		return (
			<>
				{header}
				<TextVariable
					constant={variable.spec.spec.constant}
					onChange={(value) => onChange(variable.updateValue(value))}
					value={variable.spec.spec.value}
				/>
			</>
		);
	}

	if (variable.spec.spec.plugin.kind === "StaticList" && variable.spec.spec.allowMultiple) {
		return (
			<>
				{header}
				<ListMultiChoiceVariable
					{...variable.spec.spec}
					onChange={(value) => onChange(variable.updateValue(value))}
					options={variable.spec.spec.plugin.spec.values}
				/>
			</>
		);
	}

	if (variable.spec.spec.plugin.kind === "StaticList" && !variable.spec.spec.allowMultiple) {
		return (
			<>
				{header}
				<ListSingleChoiceVariable
					{...variable.spec.spec}
					onChange={(value) => onChange(variable.updateValue(value))}
					options={variable.spec.spec.plugin.spec.values}
				/>
			</>
		);
	}

	const interpolate = (template: Template<["${", "}"], null>) => {
		return template.interpolate(templateVariables.values, {
			// do not send partially interpolated templates
			whenVariableMissing: undefined,
			// do not self-reference the variable being queried
			omit: generateVariableNames(variable.name),
		});
	};

	if (variable.spec.spec.plugin.kind === "MetricLabelNames") {
		return (
			<>
				{header}
				<MetricLabelNames
					{...variable.spec.spec}
					matchers={pipe(variable.spec.spec.plugin.spec.matchers, map(interpolate), filter(isPresent))}
					onChange={(values: DashboardVariableValue) => {
						onChange(variable.updateValue(values));
					}}
				/>
			</>
		);
	}

	if (variable.spec.spec.plugin.kind === "MetricLabelValues") {
		return (
			<>
				{header}
				<MetricLabelValues
					{...variable.spec.spec}
					label={variable.spec.spec.plugin.spec.labelName}
					matchers={pipe(variable.spec.spec.plugin.spec.matchers, map(interpolate), filter(isPresent))}
					onChange={(values: DashboardVariableValue) => {
						onChange(variable.updateValue(values));
					}}
				/>
			</>
		);
	}

	if (variable.spec.spec.plugin.kind === "MetricPromQL") {
		return (
			<>
				{header}
				<MetricsPromQLVariable
					{...variable.spec.spec}
					expression={interpolate(variable.spec.spec.plugin.spec.expr) ?? ""}
					label={variable.spec.spec.plugin.spec.labelName}
					onChange={(values: DashboardVariableValue) => {
						onChange(variable.updateValue(values));
					}}
				/>
			</>
		);
	}

	return <></>;
};
